import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/auth.service';
import { UserRole, SUPER_ADMIN_EMAIL, ADMIN_EMAIL, COMPANY_EMAIL_DOMAIN } from '../types';

// ─── Validation schemas ──────────────────────────────────────────────
const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});
const otpSchema = z.object({
  otp: z.string().length(6, 'OTP must be 6 digits'),
});
const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
});
const passwordLoginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type EmailFormData = z.infer<typeof emailSchema>;
type OTPFormData = z.infer<typeof otpSchema>;
type SignupFormData = z.infer<typeof signupSchema>;
type PasswordLoginFormData = z.infer<typeof passwordLoginSchema>;
type AuthStep = 'email' | 'otp' | 'signup';

// ─── Design tokens ───────────────────────────────────────────────────
const INK = '#0A0A0A';
const INK_HOVER = '#1F1F1F';
const DARK_BG = '#0A0A0A';
const DARK_TEXT_MUTED = '#A1A1AA';
const DARK_TEXT_SOFT = '#52525B';
const DARK_HAIRLINE = 'rgba(255,255,255,0.10)';
const SURFACE = '#FFFFFF';
const TEXT_MUTED = '#737373';
const TEXT_SOFT = '#A3A3A3';
const HAIRLINE = '#E5E5E5';

// Underline-only TextField sx (Linear / Stripe ceremonial inputs)
const underlineInputSx = {
  '& .MuiInput-root': {
    fontSize: '0.95rem',
    color: INK,
    '&:before': { borderBottomColor: HAIRLINE, borderBottomWidth: '1px' },
    '&:hover:not(.Mui-disabled):before': { borderBottomColor: '#A3A3A3', borderBottomWidth: '1px !important' },
    '&:after': { borderBottomColor: INK, borderBottomWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': {
    color: TEXT_SOFT,
    fontSize: '0.7rem',
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    transform: 'translate(0, -4px) scale(1)',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: INK },
  '& .MuiInputLabel-shrink': { transform: 'translate(0, -4px) scale(1)' },
  '& .MuiInput-input': { py: 1.2, fontWeight: 500 },
  '& .MuiFormHelperText-root': { fontSize: '0.72rem', mt: 0.5 },
};

const primaryButtonSx = {
  py: 1.4,
  fontSize: '0.88rem',
  fontWeight: 600,
  borderRadius: 1,
  textTransform: 'none' as const,
  letterSpacing: '0.01em',
  bgcolor: INK,
  color: '#FFFFFF',
  boxShadow: 'none',
  transition: 'background-color 120ms ease',
  justifyContent: 'space-between',
  px: 2.5,
  '&:hover': { bgcolor: INK_HOVER, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E5E5E5', color: TEXT_SOFT },
};

// ─── Left-pane brand strip ─────────────────────────────────────────
function LeftPane() {
  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: DARK_BG,
        color: '#FFFFFF',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        position: 'relative',
        p: 6,
        overflow: 'hidden',
      }}
    >
      {/* Soft radial accent in bottom-right (the only "color" on the dark panel) */}
      <Box
        sx={{
          position: 'absolute',
          right: -120,
          bottom: -120,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.18) 0%, rgba(16, 185, 129, 0) 70%)',
          pointerEvents: 'none',
        }}
      />
      {/* Very subtle dot grid texture */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          backgroundImage: 'radial-gradient(circle, #FFFFFF 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
        }}
      />

      {/* Eyebrow only — the brand mark lives on the right panel where its
          colors work cleanly against white. Keep the dark side as pure hero. */}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            border: `1px solid ${DARK_HAIRLINE}`,
            borderRadius: 4,
            px: 1.25,
            py: 0.5,
          }}
        >
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#22C55E', boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.16)' }} />
          <Typography sx={{ fontSize: '0.65rem', color: '#D4D4D8', letterSpacing: '0.16em', fontWeight: 500, textTransform: 'uppercase' }}>
            MIS · Portfolio Desk
          </Typography>
        </Box>
      </Box>

      {/* Hero copy — centered vertically in remaining space */}
      <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', maxWidth: 480 }}>
        <Box>
          <Typography
            sx={{
              fontSize: '2.5rem',
              fontWeight: 600,
              color: '#FFFFFF',
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              mb: 2.5,
            }}
          >
            Where positions meet accountability.
          </Typography>
          <Typography
            sx={{
              fontSize: '0.95rem',
              color: DARK_TEXT_MUTED,
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Real-time P&L, audit-grade trade history, and intraday risk monitoring — built for proprietary
            trading desks that need to know where every rupee sits, right now.
          </Typography>
        </Box>
      </Box>

      {/* Bottom stats row */}
      <Box sx={{ position: 'relative', pt: 4, borderTop: `1px solid ${DARK_HAIRLINE}` }}>
        <Stack direction="row" spacing={6}>
          <Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: '#FFFFFF', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              Realtime
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: DARK_TEXT_SOFT, letterSpacing: '0.16em', fontWeight: 500, mt: 0.6, textTransform: 'uppercase' }}>
              Tick stream
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: '#FFFFFF', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              IST
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: DARK_TEXT_SOFT, letterSpacing: '0.16em', fontWeight: 500, mt: 0.6, textTransform: 'uppercase' }}>
              Asia/Kolkata
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: '#FFFFFF', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              EOD
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: DARK_TEXT_SOFT, letterSpacing: '0.16em', fontWeight: 500, mt: 0.6, textTransform: 'uppercase' }}>
              Reconciled
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontSize: '0.62rem', color: DARK_TEXT_SOFT, letterSpacing: '0.18em', fontWeight: 500, mt: 3, textTransform: 'uppercase' }}>
          NSE · BSE · NFO · Bajaj Broking
        </Typography>
      </Box>
    </Box>
  );
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithOTP, signupWithOTP } = useAuth();

  const [authMethod, setAuthMethod] = useState<'otp' | 'password'>('otp');
  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/';

  const emailForm = useForm<EmailFormData>({ resolver: zodResolver(emailSchema), defaultValues: { email: '' } });
  const otpForm = useForm<OTPFormData>({ resolver: zodResolver(otpSchema), defaultValues: { otp: '' } });
  const signupForm = useForm<SignupFormData>({ resolver: zodResolver(signupSchema), defaultValues: { name: '' } });
  const passwordForm = useForm<PasswordLoginFormData>({
    resolver: zodResolver(passwordLoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const WHITELISTED_EMAILS = ['faisalak056@gmail.com', 'sayeed@aircomlimited.co.uk'];

  const isAuthorizedEmail = (email: string): boolean => {
    const normalized = email.toLowerCase();
    return (
      normalized === SUPER_ADMIN_EMAIL.toLowerCase() ||
      normalized === ADMIN_EMAIL.toLowerCase() ||
      normalized.endsWith(COMPANY_EMAIL_DOMAIN.toLowerCase()) ||
      WHITELISTED_EMAILS.map((e) => e.toLowerCase()).includes(normalized)
    );
  };

  const getRoleLabel = (role: UserRole): string => {
    switch (role) {
      case UserRole.SUPER_ADMIN: return 'Super Admin';
      case UserRole.ADMIN: return 'Admin';
      case UserRole.HR: return 'HR';
      case UserRole.DEALER: return 'Dealer';
      case UserRole.AGENT: return 'Agent';
      case UserRole.CLIENT: return 'Client';
      case UserRole.EMPLOYEE: return 'Employee';
      default: return 'User';
    }
  };

  const handleSendOTP = async (data: EmailFormData) => {
    setError(null); setSuccess(null);
    if (!isAuthorizedEmail(data.email)) {
      setError('Only registered employees or authorised users can sign up/login with OTP.');
      return;
    }
    setLoading(true);
    try {
      const response = await authService.sendOTP({ email: data.email, purpose: 'signup' });
      setEmail(data.email);
      setUserRole(response.role);
      setIsNewUser(true);
      setSuccess(response.message);
      setStep('otp');
    } catch (err: any) {
      if (err.code === 'ALREADY_EXISTS') {
        try {
          const loginResponse = await authService.sendOTP({ email: data.email, purpose: 'login' });
          setEmail(data.email);
          setUserRole(loginResponse.role);
          setIsNewUser(false);
          setSuccess(loginResponse.message);
          setStep('otp');
        } catch (loginErr: any) {
          setError(loginErr.message || 'Failed to send OTP');
        }
      } else {
        setError(err.message || 'Failed to send OTP');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (data: OTPFormData) => {
    setError(null); setLoading(true);
    try {
      if (isNewUser) {
        setStep('signup');
      } else {
        await loginWithOTP(email, data.otp);
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (data: SignupFormData) => {
    setError(null); setLoading(true);
    try {
      const otp = otpForm.getValues('otp');
      await signupWithOTP({ email, otp, name: data.name });
      setSuccess('Account created successfully!');
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (data: PasswordLoginFormData) => {
    setError(null); setSuccess(null); setLoading(true);
    try {
      await login(data);
      navigate(from, { replace: true });
    } catch (err: any) {
      const errorMessage = err.message || err.response?.data?.message || 'Invalid email or password';
      setError(errorMessage);
      setLoading(false);
      return;
    }
  };

  const handleBack = () => {
    setStep('email');
    setEmail('');
    setUserRole(null);
    setError(null);
    setSuccess(null);
    emailForm.reset();
    otpForm.reset();
    signupForm.reset();
  };

  const handleResendOTP = async () => {
    setError(null); setLoading(true);
    try {
      const response = await authService.sendOTP({ email, purpose: isNewUser ? 'signup' : 'login' });
      setSuccess(response.message);
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const eyebrow = step === 'email' ? 'Member access' : step === 'otp' ? 'Verify identity' : 'Finish setup';
  const heading =
    step === 'signup' ? 'Complete your profile' :
    step === 'otp' ? 'Welcome back' :
    'Welcome back';
  const subheading =
    step === 'signup' ? "A couple of details and you're in." :
    step === 'otp' ? <>We sent a 6-digit code to <Box component="span" sx={{ color: INK, fontWeight: 500 }}>{email}</Box></> :
    'Sign in with your authorised email address to continue.';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: SURFACE,
      }}
    >
      <LeftPane />

      {/* ───── Right pane (form) ───── */}
      <Box
        sx={{
          flex: { xs: 1, md: 1 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 5 },
          position: 'relative',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          {/* Brand mark — sits on the white panel where the logo's natural
              colors work cleanly. Visible on all screen sizes. */}
          <Box sx={{ mb: 5 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Percent Investments"
              sx={{
                height: 40,
                width: 'auto',
                maxWidth: 200,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>

          {/* Eyebrow */}
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              color: TEXT_SOFT,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              mb: 1.5,
            }}
          >
            {eyebrow}
          </Typography>

          {/* Back link on non-email steps */}
          {step !== 'email' && (
            <Button
              onClick={handleBack}
              size="small"
              startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none',
                color: TEXT_MUTED,
                px: 0,
                mb: 2,
                fontSize: '0.78rem',
                fontWeight: 500,
                minWidth: 'auto',
                '&:hover': { bgcolor: 'transparent', color: INK },
              }}
            >
              Back
            </Button>
          )}

          {/* Heading + subhead */}
          <Typography
            sx={{
              fontSize: '2.1rem',
              fontWeight: 600,
              color: INK,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            {heading}
          </Typography>
          <Typography sx={{ fontSize: '0.92rem', color: TEXT_MUTED, lineHeight: 1.55, mb: 4 }}>
            {subheading}
          </Typography>

          {/* Role pill */}
          {userRole && step !== 'email' && (
            <Chip
              label={`Signing in as ${getRoleLabel(userRole)}`}
              size="small"
              sx={{
                mb: 3,
                bgcolor: 'transparent',
                color: INK,
                border: `1px solid ${HAIRLINE}`,
                fontWeight: 500,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
          )}

          {/* Inline alerts (compact, no card-in-card) */}
          {success && (
            <Box
              sx={{
                mb: 3,
                pl: 1.5,
                py: 1,
                borderLeft: '2px solid #15803D',
                bgcolor: 'rgba(34, 197, 94, 0.04)',
              }}
            >
              <Typography sx={{ fontSize: '0.82rem', color: '#166534', fontWeight: 500 }}>{success}</Typography>
            </Box>
          )}
          {error && (
            <Box
              sx={{
                mb: 3,
                pl: 1.5,
                py: 1,
                borderLeft: '2px solid #B91C1C',
                bgcolor: 'rgba(239, 68, 68, 0.04)',
              }}
            >
              <Typography sx={{ fontSize: '0.82rem', color: '#991B1B', fontWeight: 500 }}>{error}</Typography>
            </Box>
          )}

          {/* Auth method tabs (email step only) */}
          {step === 'email' && (
            <Tabs
              value={authMethod}
              onChange={(_, v) => setAuthMethod(v)}
              sx={{
                mb: 3,
                minHeight: 32,
                borderBottom: `1px solid ${HAIRLINE}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  minHeight: 32,
                  color: TEXT_MUTED,
                  py: 0.5,
                  px: 0,
                  mr: 3,
                  minWidth: 'auto',
                  '&:hover': { color: INK },
                },
                '& .Mui-selected': { color: INK },
                '& .MuiTabs-indicator': { bgcolor: INK, height: 1.5 },
              }}
            >
              <Tab value="otp" label="One-time code" />
              <Tab value="password" label="Password" />
            </Tabs>
          )}

          {/* ─── Email → OTP form ─── */}
          {step === 'email' && authMethod === 'otp' && (
            <form onSubmit={emailForm.handleSubmit(handleSendOTP)}>
              <Stack spacing={3.5}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Email address"
                  type="email"
                  {...emailForm.register('email')}
                  error={!!emailForm.formState.errors.email}
                  helperText={emailForm.formState.errors.email?.message}
                  placeholder="you@percentinvestments.com"
                  InputLabelProps={{ shrink: true }}
                  sx={underlineInputSx}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                  sx={primaryButtonSx}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Continue'}
                </Button>
              </Stack>
            </form>
          )}

          {/* ─── Password login form ─── */}
          {step === 'email' && authMethod === 'password' && (
            <form onSubmit={passwordForm.handleSubmit(handlePasswordLogin)}>
              <Stack spacing={3.5}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Email address"
                  type="email"
                  {...passwordForm.register('email')}
                  error={!!passwordForm.formState.errors.email}
                  helperText={passwordForm.formState.errors.email?.message}
                  InputLabelProps={{ shrink: true }}
                  sx={underlineInputSx}
                />
                <TextField
                  fullWidth
                  variant="standard"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  {...passwordForm.register('password')}
                  error={!!passwordForm.formState.errors.password}
                  helperText={passwordForm.formState.errors.password?.message}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: TEXT_SOFT, '&:hover': { color: INK, bgcolor: 'transparent' } }}
                        >
                          {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={underlineInputSx}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                  sx={primaryButtonSx}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Sign in'}
                </Button>
              </Stack>
            </form>
          )}

          {/* ─── OTP verification ─── */}
          {step === 'otp' && (
            <form onSubmit={otpForm.handleSubmit(handleVerifyOTP)}>
              <Stack spacing={3.5}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Verification code"
                  {...otpForm.register('otp')}
                  error={!!otpForm.formState.errors.otp}
                  helperText={otpForm.formState.errors.otp?.message}
                  placeholder="• • • • • •"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    maxLength: 6,
                    inputMode: 'numeric',
                    style: { letterSpacing: '0.6em', fontSize: '1.25rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' },
                  }}
                  sx={underlineInputSx}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                  sx={primaryButtonSx}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Verify'}
                </Button>
                <Box sx={{ textAlign: 'center', pt: 0.5 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED }}>
                    Didn't get it?{' '}
                    <Box
                      component="button"
                      type="button"
                      onClick={handleResendOTP}
                      disabled={loading}
                      sx={{
                        background: 'none',
                        border: 'none',
                        color: INK,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        fontSize: '0.78rem',
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                        textDecorationColor: HAIRLINE,
                        p: 0,
                        '&:hover': { textDecorationColor: INK },
                      }}
                    >
                      Resend code
                    </Box>
                  </Typography>
                </Box>
              </Stack>
            </form>
          )}

          {/* ─── Signup (name) ─── */}
          {step === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignup)}>
              <Stack spacing={3.5}>
                <TextField
                  fullWidth
                  variant="standard"
                  label="Full name"
                  {...signupForm.register('name')}
                  error={!!signupForm.formState.errors.name}
                  helperText={signupForm.formState.errors.name?.message}
                  InputLabelProps={{ shrink: true }}
                  sx={underlineInputSx}
                />
                <Box sx={{ pt: 1, borderTop: `1px solid ${HAIRLINE}` }}>
                  <Typography sx={{ fontSize: '0.7rem', color: TEXT_SOFT, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 500, mb: 0.5 }}>
                    Account
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: INK, fontWeight: 500 }}>
                    {email}
                  </Typography>
                  {userRole && (
                    <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, mt: 0.25 }}>
                      Role · {getRoleLabel(userRole)}
                    </Typography>
                  )}
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
                  sx={primaryButtonSx}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Create account'}
                </Button>
              </Stack>
            </form>
          )}

          {/* Footer status row */}
          <Box
            sx={{
              mt: 6,
              pt: 2.5,
              borderTop: `1px solid ${HAIRLINE}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: '#22C55E',
                  boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.16)',
                }}
              />
              <Typography sx={{ fontSize: '0.65rem', color: TEXT_SOFT, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 600 }}>
                Secure session
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.65rem', color: TEXT_SOFT, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 500 }}>
              Encrypted · OTP
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
